import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * Personal notes.
 *
 * One place builds the key and one place fetches, for the same reason the
 * announcement feed does — two components spelling out a key by hand and
 * returning different shapes from it is how the dashboard crashed before.
 */

export interface Note {
  id: string;
  content: string;
  isPinned: boolean;
  /** Null while outstanding; a timestamp once ticked off. */
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NoteList {
  items: Note[];
  meta: { total: number; completed: number; active: number };
}

export type NoteFilter = 'all' | 'completed';

export const noteKeys = {
  all: () => ['notes'] as const,
  list: (filter: NoteFilter, search: string) => ['notes', 'list', filter, search] as const,
};

export function useNotes(filter: NoteFilter, search: string) {
  return useQuery({
    queryKey: noteKeys.list(filter, search),
    queryFn: async () => {
      const { data } = await api.get<NoteList>('/notes', {
        params: {
          ...(filter === 'completed' ? { filter: 'completed' } : {}),
          ...(search ? { search } : {}),
        },
      });
      return data;
    },
    // Keeps the list on screen while a search refines, instead of blanking to
    // the empty state on every keystroke.
    placeholderData: (previous) => previous,
  });
}
